import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, from, of, throwError } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { API_ENDPOINTS } from '../config/api-endpoints';
import {
  Notification as AppNotification, NotificationPage, fromServerNotification,
  DeviceToken, DeviceTokenRequest, TestPushRequest,
} from '../models';
import { AuthService } from './auth.service';

@Injectable({ providedIn: 'root' })
export class NotificationService {

  constructor(private http: HttpClient, private auth: AuthService) {}

  /**
   * The signed-in user's address. The service requires it as `recipient` and
   * refuses any address that is not the caller's own. The page used to send
   * none, so the list and the unread count both failed — and the header badge,
   * which swallows errors, sat at 0 for everyone.
   */
  private recipient(): Observable<string> {
    return from(this.auth.getCurrentUserEmail()).pipe(
      switchMap(email => email ? of(email) : throwError(() => new Error('Not signed in'))));
  }

  /** One page, newest first. The service answers with a page object, not a list. */
  getNotifications(page = 0, size = 20): Observable<AppNotification[]> {
    return this.recipient().pipe(
      switchMap(recipient => this.http.get<NotificationPage>(
        `notification:${API_ENDPOINTS.NOTIFICATIONS.LIST}`,
        { params: { recipient, pageNo: page, pageSize: size } })),
      map(body => (body?.notificationResponseList ?? []).map(fromServerNotification)));
  }

  getUnreadCount(): Observable<number> {
    return this.recipient().pipe(
      switchMap(recipient => this.http.get<number>(
        `notification:${API_ENDPOINTS.NOTIFICATIONS.UNREAD_COUNT}`, { params: { recipient } })));
  }

  /** PATCH: the service does not accept PUT here, so this used to fail every time. */
  markAsRead(id: number | string): Observable<void> {
    return this.http.patch<void>(`notification:${API_ENDPOINTS.NOTIFICATIONS.MARK_READ(id)}`, null);
  }

  /** One request for all of them, rather than one per loaded notification. */
  markAllAsRead(): Observable<number> {
    return this.recipient().pipe(
      switchMap(recipient => this.http.patch<number>(
        `notification:${API_ENDPOINTS.NOTIFICATIONS.MARK_ALL_READ}`, null, { params: { recipient } })));
  }

  registerDeviceToken(data: DeviceTokenRequest): Observable<DeviceToken> {
    return this.http.post<DeviceToken>(`notification:${API_ENDPOINTS.NOTIFICATIONS.REGISTER_TOKEN}`, data);
  }

  getUserDeviceTokens(userId: number | string): Observable<DeviceToken[]> {
    return this.http.get<DeviceToken[]>(`notification:${API_ENDPOINTS.NOTIFICATIONS.USER_TOKENS(userId)}`);
  }

  deleteDeviceToken(token: string): Observable<void> {
    return this.http.delete<void>(`notification:${API_ENDPOINTS.NOTIFICATIONS.DELETE_TOKEN(token)}`);
  }

  sendTestPush(data: TestPushRequest): Observable<void> {
    return this.http.post<void>(`notification:${API_ENDPOINTS.NOTIFICATIONS.TEST_PUSH}`, data);
  }
}
